import {
  ArithmeticOperators,
  AutomationTokenTypes,
  BracketTokens,
  LogicalOperators,
  RelationalOperators,
} from 'models/enums/Automation';
import Model from 'models/Model';

export default class AutomationRuleBuilder
  extends Model<Model.Automation.IRuleBuilder>({ icon: 'fas-cloud' })
  implements Model.Instance<Model.Automation.IRuleBuilder, false>
{
  logicalLayout: Model.Automation.ILayoutToken[];
  expressions: Model.Automation.IExpression[];
  effects: Model.Automation.IEffect[];

  constructor(
    data: Partial<Model.Automation.IRuleBuilder> | Utils.FormData<Model.Automation.IRuleBuilder>,
    language?: Locale.Languages
  ) {
    super({}, language);
    this.reset();
    this.update(data);
  }

  addExpression(expression: Omit<Model.Automation.IExpression, 'uid'>) {
    const uid = crypto.randomUUID().replace(/-/g, '');

    if (!uid) {
      throw new Error('UID is not generated!');
    }

    this.expressions.push({ ...expression, uid });

    this.logicalLayout.push({
      type: AutomationTokenTypes.Expression,
      referenceId: uid,
    });

    return this;
  }

  removeExpression(uid: string) {
    this.expressions = this.expressions.filter((expression) => expression.uid !== uid);

    this.logicalLayout = this.logicalLayout.filter(
      (token) => token.type !== AutomationTokenTypes.Expression || token.referenceId !== uid
    );

    return this;
  }

  addLogical(referenceId: Utils.ValueOf<typeof LogicalOperators>) {
    this.logicalLayout.push({
      type: AutomationTokenTypes.LogicalOperator,
      referenceId,
    });

    return this;
  }

  addArithmetic(referenceId: Utils.ValueOf<typeof ArithmeticOperators>) {
    this.logicalLayout.push({
      type: AutomationTokenTypes.ArithmeticOperator,
      referenceId,
    });

    return this;
  }

  addRelational(referenceId: Utils.ValueOf<typeof RelationalOperators>) {
    this.logicalLayout.push({
      type: AutomationTokenTypes.RelationalOperator,
      referenceId,
    });

    return this;
  }

  addBracket(referenceId: Utils.ValueOf<typeof BracketTokens>) {
    this.logicalLayout.push({
      type: AutomationTokenTypes.Bracket,
      referenceId,
    });

    return this;
  }

  addAnd() {
    return this.addLogical(LogicalOperators.And);
  }

  addOr() {
    return this.addLogical(LogicalOperators.Or);
  }

  addNot() {
    return this.addLogical(LogicalOperators.Not);
  }

  addAddition() {
    return this.addArithmetic(ArithmeticOperators.Add);
  }

  addSubtraction() {
    return this.addArithmetic(ArithmeticOperators.Subtract);
  }

  addMultiplication() {
    return this.addArithmetic(ArithmeticOperators.Multiply);
  }

  addDivision() {
    return this.addArithmetic(ArithmeticOperators.Divide);
  }

  addGreaterThan() {
    return this.addRelational(RelationalOperators.GreaterThan);
  }

  addGreaterThanOrEqual() {
    return this.addRelational(RelationalOperators.GreaterThanOrEqual);
  }

  addLessThan() {
    return this.addRelational(RelationalOperators.LessThan);
  }

  addLessThanOrEqual() {
    return this.addRelational(RelationalOperators.LessThanOrEqual);
  }

  addEqual() {
    return this.addRelational(RelationalOperators.Equal);
  }

  addNotEqual() {
    return this.addRelational(RelationalOperators.NotEqual);
  }

  openBracket() {
    return this.addBracket(BracketTokens.OpenBracket);
  }

  closeBracket() {
    return this.addBracket(BracketTokens.CloseBracket);
  }

  addEffect(effect: Model.Automation.IEffect) {
    this.effects.push(effect);
    return this;
  }

  reset(): void {
    this.logicalLayout = [];
    this.expressions = [];
    this.effects = [];
  }

  validate() {
    if (!this.logicalLayout.length) {
      throw new Error('Automation rule logical layout cannot be empty!');
    }
    if (!this.expressions?.length) {
      throw new Error('Automation rule expressions cannot be empty!');
    }
    if (!this.effects.length) {
      throw new Error('Automation rule effects cannot be empty!');
    }

    let bracketDepth = 0;

    for (const token of this.logicalLayout) {
      if (
        token.type === AutomationTokenTypes.Expression &&
        !this.expressions.some((expression) => expression.uid === token.referenceId)
      ) {
        throw new Error(`Expression UID does not exist for '${token.referenceId}'!`);
      }

      if (token.type === AutomationTokenTypes.Bracket) {
        if (token.referenceId === BracketTokens.OpenBracket) bracketDepth += 1;
        if (token.referenceId === BracketTokens.CloseBracket) bracketDepth -= 1;

        if (bracketDepth < 0) {
          throw new Error('Automation rule has a closing bracket without an opening bracket!');
        }
      }
    }

    if (bracketDepth !== 0) {
      throw new Error('Automation rule has unbalanced brackets!');
    }
  }

  build(): Model.Automation.IRule {
    this.validate();

    return {
      logicalLayout: this.logicalLayout,
      expressions: this.expressions,
      effects: this.effects,
    };
  }
}
